import { doc, getDoc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { auth, dbAccounts, storage } from "../../../../backend/firebaseConfig";

const COLLECTION_NAME = "MonthlyFinplanStatements";

const validateMonth = (month) => {
  if (!/^\d{4}-\d{2}$/.test(month || "")) {
    throw new Error("Select a valid statement month.");
  }
};

export const subscribeFinplanStatement = (month, onData, onError) => {
  validateMonth(month);
  return onSnapshot(
    doc(dbAccounts, COLLECTION_NAME, month),
    (snapshot) =>
      onData(snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null),
    onError,
  );
};

export const getFinplanStatement = async (month) => {
  validateMonth(month);
  const snapshot = await getDoc(doc(dbAccounts, COLLECTION_NAME, month));
  return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
};

export const saveFinplanStatement = async ({
  month,
  file,
  headers,
  rows,
  previousStoragePath = "",
}) => {
  validateMonth(month);
  if (!file) throw new Error("Select a statement to upload.");
  if (!headers?.length || !rows?.length) {
    throw new Error("The selected file does not contain any statement entries.");
  }

  const extension = file.name.split(".").pop()?.toLowerCase() || "xlsx";
  const storagePath = `MonthlyFinplanStatements/${month}/finplan-statement.${extension}`;
  const storageReference = ref(storage, storagePath);
  await uploadBytes(storageReference, file, {
    contentType: file.type || "application/octet-stream",
    contentDisposition: `attachment; filename="${file.name.replace(/["\r\n]/g, "")}"`,
    customMetadata: { statementMonth: month },
  });
  const fileUrl = await getDownloadURL(storageReference);
  if (previousStoragePath && previousStoragePath !== storagePath) {
    await deleteObject(ref(storage, previousStoragePath)).catch(() => {});
  }

  const user = auth.currentUser;
  await setDoc(
    doc(dbAccounts, COLLECTION_NAME, month),
    {
      month,
      fileName: file.name,
      fileUrl,
      storagePath,
      fileContentType: file.type || "",
      fileSize: Number(file.size) || 0,
      headers,
      rows,
      rowCount: rows.length,
      uploadedAt: serverTimestamp(),
      uploadedBy: {
        uid: user?.uid || "",
        email: user?.email || "",
        name: user?.displayName || user?.email || "",
      },
    },
    { merge: true },
  );
};

export const saveFinplanAdviserLinks = async ({
  month,
  field,
  links,
}) => {
  validateMonth(month);
  if (!["recipientLinks", "sellingAdviserLinks"].includes(field)) {
    throw new Error("Select a valid Finplan adviser link type.");
  }
  await setDoc(
    doc(dbAccounts, COLLECTION_NAME, month),
    {
      [field]: links,
      adviserLinksUpdatedAt: serverTimestamp(),
    },
    { merge: true },
  );
};
